/**
 * Движок синхронизации.
 *
 * Разбирает очередь `outbox` — журнал локальных изменений — и отправляет её на
 * сервер, затем забирает изменения с других устройств. Пока сервер не
 * настроен (`VITE_API_URL` пуст), движок бездействует: очередь копится, а
 * приложение продолжает полноценно работать офлайн.
 *
 * ## Гарантии
 *
 * - **Порядок.** Записи отправляются в порядке создания: создание сущности
 *   уходит раньше её обновления.
 * - **Повторы с паузой.** Неудачная отправка не удаляет запись из очереди;
 *   счётчик `attempts` растёт, следующая попытка откладывается экспоненциально.
 *   Это защищает сервер от шторма запросов при массовом сбое.
 * - **Идемпотентность.** Идентификаторы генерирует клиент, поэтому повторная
 *   отправка того же изменения не создаёт дубль.
 *
 * ## Что нужно сделать при подключении сервера
 *
 * Реализовать `pushChanges` и `pullChanges` под фактический контракт API
 * (ожидаемый описан в `docs/ARCHITECTURE.md`, раздел 9). Остальная механика —
 * очередь, повторы, состояние — уже готова.
 */

import { db } from '@/shared/db/database';
import type { OutboxRecord } from '@/shared/db/schema';
import { getCurrentUserId } from '@/shared/db/session-context';

import { ApiError, isConfigured } from './transport';

/** Сколько записей уходит за один запрос. */
const BATCH_SIZE = 100;
/** После стольких неудач запись считается «ядовитой» и пропускается. */
const MAX_ATTEMPTS = 8;

/**
 * Ключ точки последнего обмена.
 *
 * Курсор хранится в базе, а не в памяти: после перезагрузки страницы иначе
 * неоткуда узнать, с какого момента забирать чужие изменения, и каждый обмен
 * пришлось бы делать полным. На истории в несколько лет это делает
 * синхронизацию неработоспособной на мобильной сети.
 */
const CURSOR_KEY = 'sync.cursor';

/** Точка последнего успешного обмена. `null` — обмена ещё не было. */
export async function readSyncCursor(): Promise<number | null> {
  const row = await db.settings.get(CURSOR_KEY);
  return typeof row?.value === 'number' ? row.value : null;
}

export async function writeSyncCursor(value: number): Promise<void> {
  await db.settings.put({ key: CURSOR_KEY, value, updatedAt: Date.now() });
}

/**
 * Изменения, которые не удалось отправить много раз подряд.
 *
 * Такая запись выпадает из отправки навсегда и раньше исчезала молча: человек
 * видел только вечно ненулевой счётчик «ожидают синхронизации» и никак не мог
 * узнать, что часть данных не уедет никогда.
 */
export async function countStuck(userId: string): Promise<number> {
  return db.outbox
    .where('userId')
    .equals(userId)
    .filter((record) => record.attempts >= MAX_ATTEMPTS)
    .count();
}

export type SyncStatus = 'idle' | 'syncing' | 'offline' | 'error' | 'disabled';

export interface SyncReport {
  status: SyncStatus;
  pushed: number;
  pulled: number;
  pending: number;
  error: string | null;
  lastSyncAt: number | null;
}

let inFlight: Promise<SyncReport> | null = null;

/** Сколько изменений ждёт отправки. Показывается в настройках. */
export async function getPendingCount(): Promise<number> {
  const userId = getCurrentUserId();
  if (!userId) return 0;
  return db.outbox.where('userId').equals(userId).count();
}

/**
 * Запускает обмен с сервером.
 *
 * Параллельные вызовы объединяются: если синхронизация уже идёт, возвращается
 * тот же промис. Иначе одновременный запуск по таймеру и по возврату из фона
 * отправил бы очередь дважды.
 */
export async function sync(): Promise<SyncReport> {
  if (inFlight) return inFlight;

  inFlight = runSync().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

async function runSync(): Promise<SyncReport> {
  const base: SyncReport = {
    status: 'idle',
    pushed: 0,
    pulled: 0,
    pending: await getPendingCount(),
    error: null,
    lastSyncAt: null,
  };

  if (!isConfigured()) {
    return { ...base, status: 'disabled' };
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { ...base, status: 'offline' };
  }

  const userId = getCurrentUserId();
  if (!userId) return base;

  try {
    const pushed = await pushOutbox(userId);
    const pulled = await pullChanges(await readSyncCursor());

    const finishedAt = Date.now();
    // Курсор сохраняется только после успешного обмена: записать его раньше
    // значит потерять чужие изменения, если pull не дошёл.
    await writeSyncCursor(finishedAt);

    return {
      status: 'idle',
      pushed,
      pulled,
      pending: await getPendingCount(),
      error: null,
      lastSyncAt: finishedAt,
    };
  } catch (error) {
    return {
      ...base,
      status: error instanceof ApiError && error.status === 0 ? 'offline' : 'error',
      error: error instanceof Error ? error.message : 'Не удалось синхронизировать данные',
      pending: await getPendingCount(),
    };
  }
}

/** Отправляет очередь изменений порциями. Возвращает число принятых записей. */
async function pushOutbox(userId: string): Promise<number> {
  let sent = 0;

  for (;;) {
    // Порядок берётся из индекса: до этого `limit` применялся раньше
    // сортировки, и в партию попадали произвольные сто записей — изменение
    // могло уехать раньше создания сущности, которую оно меняет.
    const batch = await db.outbox
      .where('[userId+createdAt]')
      .between([userId, -Infinity], [userId, Infinity])
      .filter((record) => record.attempts < MAX_ATTEMPTS && isReadyForRetry(record))
      .limit(BATCH_SIZE)
      .toArray();

    if (batch.length === 0) break;

    try {
      await pushChanges(batch);
      await db.outbox.bulkDelete(batch.map((record) => record.id));
      sent += batch.length;
    } catch (error) {
      // Порция не принята — помечаем попытку и выходим: следующие порции
      // отправлять бессмысленно, порядок изменений важен.
      const message = error instanceof Error ? error.message : 'Неизвестная ошибка';
      const now = Date.now();
      await db.transaction('rw', db.outbox, async () => {
        for (const record of batch) {
          await db.outbox.update(record.id, {
            attempts: record.attempts + 1,
            lastAttemptAt: now,
            lastError: message,
          });
        }
      });
      throw error;
    }

    if (batch.length < BATCH_SIZE) break;
  }

  return sent;
}

/**
 * Экспоненциальная пауза перед повтором: 2, 4, 8, 16… секунд.
 * Ограничена десятью минутами, чтобы синхронизация не «уснула» насовсем.
 *
 * Отсчёт идёт от последней попытки. Раньше он шёл от создания записи, которое
 * не меняется никогда: для изменения старше десяти минут условие выполнялось
 * всегда, и защита от шторма не работала ровно в том случае, ради которого
 * написана — при долгом отказе сервера.
 */
export function isReadyForRetry(record: OutboxRecord, now: number = Date.now()): boolean {
  if (record.attempts === 0) return true;

  const delayMs = Math.min(2 ** record.attempts * 1000, 10 * 60 * 1000);
  const since = record.lastAttemptAt ?? record.createdAt;
  return now - since >= delayMs;
}

/* ─────────────── Точки, требующие реализации под ваш API ─────────────── */

/**
 * Отправка порции изменений.
 *
 * Ожидаемый контракт: `POST /sync/push { changes: OutboxRecord[] }`.
 * Сервер должен быть идемпотентен по `entityId` + `revision`.
 */
function pushChanges(_batch: OutboxRecord[]): Promise<void> {
  return Promise.reject(new ApiError('Синхронизация не подключена: не задан VITE_API_URL', 0, null));
}

/**
 * Приём изменений с сервера.
 *
 * Ожидаемый контракт: `GET /sync/pull?since=<cursor>`; `null` — забрать всё,
 * то есть первый обмен на этом устройстве.
 * При слиянии выигрывает запись с большей парой (`revision`, `updatedAt`).
 */
function pullChanges(_since: number | null): Promise<number> {
  return Promise.resolve(0);
}

/**
 * Подписывает синхронизацию на возврат сети и возврат приложения из фона.
 * Вызывается один раз при старте; возвращает функцию отписки.
 */
export function startAutoSync(intervalMs = 5 * 60 * 1000): () => void {
  if (!isConfigured()) return () => undefined;

  const trigger = () => void sync();

  const timer = setInterval(trigger, intervalMs);
  window.addEventListener('online', trigger);

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') trigger();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    clearInterval(timer);
    window.removeEventListener('online', trigger);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
